// @ts-check
import React from 'react';
import { useSelector } from 'react-redux';

import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import SuspenseFallback from 'components/routers/RouteHandler/SuspenseFallback';
import EnvironmentError from 'components/Restrictions/RestrictionEnvironmentError';
import AppConfigError from 'pages/error/AppConfigError';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import useIsOfficeIp from 'modules/queries/network/util/useIsOfficeIp';
import useWhatismyip from 'modules/queries/utilities/info/useWhatismyip';
import PropTypes from 'prop-types';

const Container = ({ children }: { children: React.ReactNode }) => (
	<Stack
		sx={{
			alignItems: 'center',
			justifyContent: 'center',
			height: '100vh',
		}}
	>
		{children}
	</Stack>
);

const AppConfigGate = ({ children }: { children: React.ReactNode }) => {
	const appConfig = useSelector(appConfigSelectors.getAppConfig);
	const isProd = fromApiBoolean(process.env.REACT_APP_ISPROD);
	const { data: ipData, error: whatismyipHasError } = useWhatismyip({
		enabled: !isProd,
	});

	const { data: isOfficeIpData, isLoading: officeIpIsLoading } = useIsOfficeIp(
		{ ip: ipData?.ip || '' },
		{ enabled: Boolean(ipData?.ip) && !isProd },
	);
	const isOfficeIp = fromApiBoolean(isOfficeIpData?.isOfficeIp);
	const officeIpHasData = Boolean(isOfficeIpData);

	if (!appConfig)
		return (
			<Container>
				<AppConfigError />
			</Container>
		);

	// Nothing in this block happens in production, so let's not be too picky about the loader handling.
	if (!isOfficeIp && !isProd) {
		if ((officeIpIsLoading || !officeIpHasData) && !whatismyipHasError)
			return (
				<Container>
					<SuspenseFallback />
				</Container>
			);

		return (
			<Container>
				<EnvironmentError sx={{ flexGrow: 1, width: '100%' }} />
			</Container>
		);
	}

	return <>{children}</>;
};

AppConfigGate.propTypes = {
	children: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.node),
		PropTypes.node,
	]).isRequired,
};

export default AppConfigGate;
